import axios from 'axios'
import { rawAxios } from '@/main.js'   // instancia sin interceptores
import { clone, objectSnakeToCamelCase } from '@/util/libUtil.js'
import general from '@/store/modules/general.js'

const state = {
    initialized: false,
    isLoadingLogin: false,
    isAuthenticated: false,   // replaces accessToken / refreshToken
    rol: null,
    isAdmin: false,
    nombre: null,
    id: null,
    groups: [],
    INITIALISE_INFO: [
        'cuenta',
        'origenesBbdd',
        'opciones',
        'tenantInfo',
        'communicates'
    ],
    paths: {
        ApiTokenPath: '/api/token/',
        RefreshTokenPath: '/api/token-refresh/',
        LogoutPath: '/api/logout/',
        UserDetailPath: '/api/cuentas/detalle/',
        getPermissionsPath: '/api/get-group-permissions',
        checkinvalidPunchInPath: '/api/cuentas/check-invalid-punch-in/',
        axesAdminPath: '/api/axes-admin/',
    }
}

const mutations = {
    setAuthenticated(state, val) {
        state.isAuthenticated = val
    },
    updateUserDetails(state, { isAdmin, rol, nombre, id, groups }) {
        state.isAdmin = isAdmin
        state.rol = rol
        state.nombre = nombre
        state.id = id
        state.groups = groups
    },
    destroySession(state) {
        state.initialized = false
        state.isAuthenticated = false
        state.rol = null
        state.isAdmin = false
        state.nombre = null
        state.id = null
        state.groups = []

        const keysToPreserve = ['app_dark_mode']
        // Limpia localStorage excepto las claves que queremos preservar
        Object.keys(localStorage).forEach(key => {
            if (!keysToPreserve.includes(key)) {
                localStorage.removeItem(key)
            }
        })
    },
    isLoadingLogin(state, val) {
        state.isLoadingLogin = val
    }
}

const actions = {
    async checkGroupPermission({ state, getters }, groups) {
        const resp = await axios.get(
            getters.getEndpointFromPath(state.paths.getPermissionsPath),
            { params: { 'permission_name': groups } }
        )
        return resp.data
    },

    async userLogOut({ commit, state, getters }) {
        try {
            // rawAxios evita que un 401 aquí dispare otro ciclo de refresh/logout
            await rawAxios.post(getters.getEndpointFromPath(state.paths.LogoutPath))
        } catch (_) {
            // Best-effort — limpiar estado local independientemente
        }
        commit('destroySession')
    },
    userLogin(context, { username, password }) {
        return new Promise((resolve, reject) => {
            axios.post(context.getters.ApiTokenEndpoint, { username, password })
                .then(async ({ data }) => {
                    // Cookies are now set by the server response.
                    // Run the full initialise to load user data.
                    if (data.mfa_required) {
                        return resolve({ mfa_required: true, mfa_token: data.mfa_token})
                    }
                    await context.dispatch('initialise')
                    resolve()
                })
                .catch(reject)
        })
    },

    // Called by the 401 interceptor — just hits the refresh endpoint.
    // The browser sends the refresh cookie automatically; the server
    // responds with a new access cookie. No token handling needed here.
    refreshToken({ state, getters }) {
        if (state.isLoadingLogin) return
        const unintercepted = axios.create({ withCredentials: true })
        return unintercepted.post(getters.RefreshTokenEndpoint)
    },
    resetAxesLockout({ state, getters }, { username }) {
        return axios.post(getters.getEndpointFromPath(state.paths.axesAdminPath), { username })
    },
    getUserDetails(context) {
        return new Promise((resolve, reject) => {
            axios.get(context.getters.getEndpointFromPath(context.state.paths.UserDetailPath))
                .then(response => {
                    const d = response.data
                    const nombre = d.nombre_completo || d.username
                    context.commit('updateUserDetails', {
                        isAdmin: d.isAdmin,
                        rol: d.rol.id,
                        nombre,
                        id: d.id,
                        groups: d.tenant_groups
                    })
                    context.commit('setAuthenticated', true)
                    localStorage.setItem('cuenta', JSON.stringify(objectSnakeToCamelCase(d)))
                    if (!localStorage.getItem('meta') || !('date' in JSON.parse(localStorage.getItem('meta')))) {
                        context.dispatch(
                            'rrhh/fetchCommunicates',
                            { created_on: new Date().toISOString().slice(0, 10), receivers: d.id, is_read: 0 },
                            { root: true }
                        )
                    }
                    resolve()
                })
                .catch(error => {
                    // A 401 here means no valid session cookie — user is not logged in
                    context.commit('setAuthenticated', false)
                    reject(error)
                })
        })
    },
    getCheckinvalidPunchIn(context) {
        return new Promise((resolve, reject) => {
            axios.get(context.getters.getEndpointFromPath(context.state.paths.checkinvalidPunchInPath))
                .then(response => {
                    localStorage.setItem('invalidPunchIn', JSON.stringify(response.data))
                    resolve()
                })
                .catch(reject)
        })
    },
    async initialise({ state, dispatch, commit, getters }) {
        const GROUPS = general.GROUPS
        if (state.isLoadingLogin) return

        return new Promise((resolve) => {
            // Mark as initialised immediately so the router doesn't
            // trigger a second initialise while this one is in flight
            state.initialized = true

            setTimeout(async () => {
                commit('isLoadingLogin', true)
                try {
                    // getUserDetails will succeed only if a valid access cookie exists.
                    // If it fails with 401, the axios response interceptor will attempt
                    // a token refresh before this catch runs.
                    const initialiseFuncs = [
                        dispatch('getUserDetails'),
                        dispatch('util/fetchTenantInfo', null, { root: true }),
                        dispatch('util/fetchOpciones', null, { root: true }),
                        getters.ckeckGroupPermission([GROUPS.PUNCH_IN]) && dispatch('getCheckinvalidPunchIn'),
                    ]

                    await Promise.all(initialiseFuncs)
                    if (getters.isDistributor) {
                        await dispatch('util/fetchOrigenesBBDD', null, { root: true })
                    }
                    localStorage.setItem('meta', JSON.stringify({ date: new Date(), readCommunicates: false }))
                } catch (_) {
                    // Not authenticated — isAuthenticated stays false.
                    // The router guard will redirect to login.
                } finally {
                    commit('isLoadingLogin', false)
                    resolve()
                }
            }, 100)
        })
    },
}

const getters = {
    isEnforceMFA() {
        try {
            return JSON.parse(localStorage.getItem('tenantInfo'))?.enforce_mfa
        } catch { return false }
    },
    isSharedClVwTenant() {
        try {
            return JSON.parse(localStorage.getItem('tenantInfo'))?.client_view_mode === general.ClientViewMode.SHARED
        } catch { return false }
    },
    isReducedClVwTenant() {
        try {
            return JSON.parse(localStorage.getItem('tenantInfo'))?.client_view_mode === general.ClientViewMode.REDUCED
        } catch { return false }
    },
    isPerAccountClVwTenant() {
        try {
            return JSON.parse(localStorage.getItem('tenantInfo'))?.client_view_mode === general.ClientViewMode.PER_ACCOUNT
        } catch { return false }
    },
    isJustAdminClVwTenant() {
        try {
            return JSON.parse(localStorage.getItem('tenantInfo'))?.client_view_mode === general.ClientViewMode.JUST_ADMIN
        } catch { return false }
    },
    hasClientsPermission(_state, getters) {
        return getters.isSharedClVwTenant || (getters.isJustAdminClVwTenant && (getters.hasAdminPermission || getters.hasSuperAdminPermission))
    },
    isDistributor() {
        try {
            return JSON.parse(localStorage.getItem('tenantInfo'))?.type === 'distributor'
        } catch { return false }
    },
    isAvatel() {
        try {
            return JSON.parse(localStorage.getItem('tenantInfo'))?.name === 'avatel'
        } catch { return false }
    },
    getInitialisePaths: (state) => state.INITIALISE_INFO,

    // Auth is now driven by server-confirmed state, not token presence
    loggedIn: (state) => state.isAuthenticated,

    ckeckGroupPermission: (state) => (groups) => {
        if (!groups?.length) return true
        const userGroups = state.groups.map(g => g.name)
        return groups.some(g => userGroups.includes(g))
    },
    checkRolePermission: (_state, getters) => (rol) => {
        if (!rol) return true
        return ({
            1: getters.hasSuperAdminPermission,
            2: getters.hasAdminPermission,
            3: getters.hasManagerPermission,
            4: getters.hasComercialPermission,
        })[rol]
    },
    hasPermission: (state, _u, rootState) => accessAllowedRoles => {
        if (!accessAllowedRoles) return false
        return accessAllowedRoles.some(role => rootState.general.rolesEncode[role] === state.rol)
    },
    getEndpointFromPath: (_s, _u, rootState) => path => rootState.general.host + path,
    ApiTokenEndpoint: (state, _u, rootState) => rootState.general.host + state.paths.ApiTokenPath,
    RefreshTokenEndpoint: (state, _u, rootState) => rootState.general.host + state.paths.RefreshTokenPath,
    hasComercialPermission: (state, _u, rootState) =>
        state.rol && state.rol === rootState.general.rolesEncode.Comercial,
    hasManagerPermission: (state, _u, rootState) =>
        state.isAdmin || (state.rol && state.rol <= rootState.general.rolesEncode.Manager),
    hasAdminPermission: (state, _u, rootState) =>
        state.isAdmin || (state.rol && state.rol <= rootState.general.rolesEncode.Admin),
    hasSuperAdminPermission: (state, _u, rootState) =>
        state.isAdmin || (state.rol && state.rol <= rootState.general.rolesEncode.SuperAdmin),
    hasOnlyManagerPermission: (state, _u, rootState) =>
        state.rol && state.rol === rootState.general.rolesEncode.Manager,
    isComercialColaborador: (state, _u, rootState) => {
        const cuenta = JSON.parse(localStorage.getItem('cuenta'))
        return state.rol === rootState.general.rolesEncode.Comercial && cuenta?.tipoComercial === 'COLABORADOR'
    },
    isComercialRecomendador: (state, _u, rootState) => {
        const cuenta = JSON.parse(localStorage.getItem('cuenta'))
        return state.rol === rootState.general.rolesEncode.Comercial && cuenta?.tipoComercial === 'RECOMENDADOR'
    },
    isComercialCobOrRec: (_s, getters) =>
        getters.isComercialColaborador || getters.isComercialRecomendador,
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}